import { Link } from "react-router-dom"
import { Button } from "../components/ui/button"
import { Calendar, CheckCircle, ChevronRight } from "lucide-react"
import { Badge } from "../components/ui/badge"
import SEO from "./SEO"

export default function DiagnosticsPage() {
  const diagnosticsSchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals Diagnostics",
    "url": "https://pranaam.pranaamhospitals.in/diagnostics",
    "logo": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals Diagnostics provides advanced diagnostic services including pathology, imaging, and cardiac diagnostics with accurate and timely results in Hyderabad.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "diagnostic services",
        "telephone": "+91-99660-60000",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      }
    ],
    "parentOrganization": {
      "@type": "MedicalOrganization",
      "name": "Pranaam Hospitals",
      "url": "https://pranaam.pranaamhospitals.in/"
    }
  },

  {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pranaam.pranaamhospitals.in/diagnostics"
    },
    "headline": "Advanced Diagnostic Services at Pranaam Hospitals",
    "description": "Explore comprehensive diagnostic services at Pranaam Hospitals including blood tests, imaging, MRI, CT scans, cardiac diagnostics, and walk-in testing.",
    "author": {
      "@type": "Organization",
      "name": "Pranaam Hospitals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pranaam Hospitals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png"
      }
    },
    "datePublished": "2015-01-01",
    "dateModified": "2025-04-01",
    "keywords": [
      "diagnostic center Hyderabad",
      "MRI CT scan Hyderabad",
      "pathology lab Hyderabad",
      "cardiac diagnostics Hyderabad"
    ],
    "articleSection": [
      "Pathology & Blood Tests",
      "Imaging Services",
      "Cardiac Diagnostics",
      "Walk-in Diagnostic Testing"
    ]
  }
];

  return (
    <>
    <SEO
  title="Advanced Diagnostics in Hyderabad | Pranaam Hospitals"
  description="Pranaam Hospitals offers advanced diagnostic services in Hyderabad including blood tests, MRI, CT scan, X-ray, cardiac diagnostics, and walk-in testing with accurate results."
  canonical="https://pranaam.pranaamhospitals.in/diagnostics"
  robots="index, follow"
  author="Pranaam Hospitals"
  publisher="Pranaam Hospitals"
  keywords="Pranaam Hospitals diagnostics, diagnostic center Hyderabad,
            MRI scan Hyderabad, CT scan Hyderabad, blood tests Hyderabad,
            cardiac diagnostics Hyderabad, pathology lab Hyderabad,
            advanced imaging services Hyderabad"
  schema={diagnosticsSchema}
/>

    <main className="min-h-screen">
      {/* Hero Section */}
      <section className="py-12 bg-gray-100">
        <div className="container px-4 mx-auto">
          <div className="grid items-center grid-cols-1 gap-8 md:grid-cols-2">
            <div>
              <Badge className="mb-2">Diagnostics</Badge>
              <h1 className="mb-4 text-3xl font-bold md:text-4xl">Advanced Diagnostic Services</h1>
              <p className="mb-6 text-muted-foreground">
                Our state-of-the-art diagnostic center offers a comprehensive range of tests and imaging services with
                quick and accurate results.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>Complete Blood Tests & Pathology</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>Advanced Imaging (MRI, CT Scan, X-Ray)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>Cardiac Diagnostics</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>Walk-in Testing Available</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-4">
                <Button className="gap-2" asChild>
                  <Link to="/book-appointment">
                    Book a Test
                    <Calendar className="w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  className="gap-2 border-primary text-primary hover:bg-primary hover:text-white"
                  asChild
                >
                  <Link to="/diagnostics/all-tests">
                    View All Tests
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative h-64 overflow-hidden rounded-lg md:h-80">
              <img
                src="/placeholder.svg?height=400&width=600&text=Advanced+Diagnostic+Services"
                alt="Advanced Diagnostic Services"
                width={600}
                height={400}
                className="object-contain rounded-lg"
              />
              <div className="absolute top-[40%] right-[42%]">
                            <h1>600 × 600 px</h1>
                          </div>
            </div>
          </div>
        </div>
      </section>

      {/* Test Categories */}
      <section className="py-12 bg-white">
        <div className="container px-4 mx-auto">
          <h2 className="mb-8 text-2xl font-bold text-center md:text-3xl">Our Diagnostic Services</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="p-6 bg-white border rounded-lg shadow-sm">
              <h3 className="mb-4 text-xl font-bold">Laboratory Tests</h3>
              <ul className="space-y-2">
                <li>Complete Blood Count</li>
                <li>Liver Function Test</li>
                <li>Kidney Function Test</li>
                <li>Lipid Profile</li>
                <li>Blood Sugar Tests</li>
                <li>Thyroid Function Tests</li>
                <li>Vitamin Levels</li>
              </ul>
            </div>
            <div className="p-6 bg-white border rounded-lg shadow-sm">
              <h3 className="mb-4 text-xl font-bold">Imaging Services</h3>
              <ul className="space-y-2">
                <li>X-Ray</li>
                <li>Ultrasound</li>
                <li>CT Scan</li>
                <li>MRI</li>
                <li>Mammography</li>
                <li>Bone Density Scan</li>
                <li>PET Scan</li>
              </ul>
            </div>
            <div className="p-6 bg-white border rounded-lg shadow-sm">
              <h3 className="mb-4 text-xl font-bold">Cardiac Diagnostics</h3>
              <ul className="space-y-2">
                <li>Electrocardiogram (ECG)</li>
                <li>Echocardiogram</li>
                <li>Stress Test</li>
                <li>Holter Monitoring</li>
                <li>Cardiac CT</li>
                <li>Cardiac MRI</li>
                <li>Angiography</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Health Packages */}
      <section className="py-12 bg-gray-100">
        <div className="container px-4 mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-4 text-2xl font-bold md:text-3xl">Diagnostic Health Packages</h2>
            <p className="mb-8 text-muted-foreground">
              Our comprehensive health packages are designed to provide a thorough assessment of your health status at
              affordable prices.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="p-6 bg-white rounded-lg shadow-sm">
              <h3 className="mb-2 text-xl font-bold">Basic Health Check</h3>
              <p className="mb-4 text-2xl font-bold text-primary">₹1,999</p>
              <ul className="mb-6 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Complete Blood Count</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Blood Sugar (Fasting & PP)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Lipid Profile</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Liver Function Test</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Kidney Function Test</span>
                </li>
              </ul>
              <Button className="w-full" asChild>
                <Link to="/book-appointment">Book Now</Link>
              </Button>
            </div>
            <div className="p-6 bg-white rounded-lg shadow-sm border-2 border-primary relative">
              <div className="absolute top-0 right-0 px-3 py-1 text-xs font-bold text-white transform translate-y-[-50%] bg-primary rounded-full">
                Popular
              </div>
              <h3 className="mb-2 text-xl font-bold">Comprehensive Check</h3>
              <p className="mb-4 text-2xl font-bold text-primary">₹4,999</p>
              <ul className="mb-6 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>All Basic Health Check Tests</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Thyroid Profile</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Vitamin B12 & D3</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Chest X-Ray</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>ECG</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Ultrasound Abdomen</span>
                </li>
              </ul>
              <Button className="w-full" asChild>
                <Link to="/book-appointment">Book Now</Link>
              </Button>
            </div>
            <div className="p-6 bg-white rounded-lg shadow-sm">
              <h3 className="mb-2 text-xl font-bold">Cardiac Health Check</h3>
              <p className="mb-4 text-2xl font-bold text-primary">₹7,999</p>
              <ul className="mb-6 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>All Comprehensive Check Tests</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>2D Echocardiogram</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Stress Test</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Carotid Doppler</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                  <span>Cardiac Risk Markers</span>
                </li>
              </ul>
              <Button className="w-full" asChild>
                <Link to="/book-package/cardiac">Book Now</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-white">
        <div className="container px-4 mx-auto">
          <h2 className="mb-8 text-2xl font-bold text-center md:text-3xl">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="p-6 bg-white border rounded-lg shadow-sm">
              <h3 className="mb-2 text-lg font-bold">Do I need to fast before my blood test?</h3>
              <p className="text-muted-foreground">
                For most blood tests like lipid profile and glucose tests, fasting for 8-12 hours is recommended. Our
                staff will inform you about any specific preparations needed for your test when you book your
                appointment.
              </p>
            </div>
            <div className="p-6 bg-white border rounded-lg shadow-sm">
              <h3 className="mb-2 text-lg font-bold">How long will it take to get my test results?</h3>
              <p className="text-muted-foreground">
                Most routine blood test results are available within 24 hours. Imaging reports are typically ready
                within 24-48 hours. For specialized tests, it may take 3-5 working days. You can access your reports
                online or collect them from our center.
              </p>
            </div>
            <div className="p-6 bg-white border rounded-lg shadow-sm">
              <h3 className="mb-2 text-lg font-bold">Can I walk in for tests or do I need an appointment?</h3>
              <p className="text-muted-foreground">
                We accept walk-ins for most routine tests. However, for specialized tests and imaging services, we
                recommend booking an appointment to avoid waiting times. You can easily book your appointment online or
                by calling our center.
              </p>
            </div>
            <div className="p-6 bg-white border rounded-lg shadow-sm">
              <h3 className="mb-2 text-lg font-bold">Are home sample collections available?</h3>
              <p className="text-muted-foreground">
                Yes, we offer home sample collection services for blood tests and other pathology samples. You can book
                this service online or by calling our customer care number. A nominal fee applies for home collection
                services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-primary text-white">
        <div className="container px-4 mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-4 text-2xl font-bold md:text-3xl">Book Your Diagnostic Test Today</h2>
            <p className="mb-6">
              Take a proactive step towards better health. Our state-of-the-art diagnostic center is equipped to provide
              you with accurate and timely results.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/book-appointment">
                  <Calendar className="mr-2 h-5 w-5" />
                  Book a Test
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-transparent text-white border-white" asChild>
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>
    </>
  )
}
